import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { Toaster } from "./components/ui/sonner";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import ChatBot from "./components/ChatBot";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import AuthCallback from "./pages/AuthCallback";
import Browse from "./pages/Browse";
import ListingDetail from "./pages/ListingDetail";
import CreateListing from "./pages/CreateListing";
import CreatePartListing from "./pages/CreatePartListing";
import SellChoice from "./pages/SellChoice";
import Dashboard from "./pages/Dashboard";
import Messages from "./pages/Messages";
import KYCVerification from "./pages/KYCVerification";
import EditListing from "./pages/EditListing";
import AdminDashboard from "./pages/AdminDashboard";
import DealerDashboard from "./pages/DealerDashboard";
import Help from "./pages/Help";
import Legal from "./pages/Legal";
import SellerProfile from "./pages/SellerProfile";
import BuySlots from "./pages/BuySlots";
import PayoutSettings from "./pages/PayoutSettings";
import Blog, { BlogPost } from "./pages/Blog";
import Trust from "./pages/Trust";
import About from "./pages/About";
import Press from "./pages/Press";
import Careers from "./pages/Careers";
import AutoTrustIndex from "./pages/AutoTrustIndex";
import Referral from "./pages/Referral";
import Disputes, { DisputeDetail } from "./pages/Disputes";
import CityLanding from "./pages/CityLanding";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check for session_id in hash fragment OR query params synchronously (before any route renders)
  // This must happen BEFORE ProtectedRoute checks, otherwise it redirects to login
  const hasSessionId = location.hash?.includes('session_id=') || 
                       location.search?.includes('session_id=');
  if (hasSessionId) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<><Navbar /><Landing /><Footer /></>} />
      <Route path="/login" element={<><Navbar /><Login /><Footer /></>} />
      <Route path="/register" element={<><Navbar /><Register /><Footer /></>} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/browse" element={<><Navbar /><Browse /><Footer /></>} />
      <Route path="/listing/:id" element={<><Navbar /><ListingDetail /><Footer /></>} />
      <Route path="/sell" element={<ProtectedRoute><Navbar /><SellChoice /><Footer /></ProtectedRoute>} />
      <Route path="/create-listing" element={<ProtectedRoute><Navbar /><CreateListing /><Footer /></ProtectedRoute>} />
      <Route path="/create-part-listing" element={<ProtectedRoute><Navbar /><CreatePartListing /><Footer /></ProtectedRoute>} />
      <Route path="/edit-listing/:id" element={<ProtectedRoute><Navbar /><EditListing /><Footer /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><Navbar /><Dashboard /><Footer /></ProtectedRoute>} />
      <Route path="/kyc" element={<ProtectedRoute><Navbar /><KYCVerification /><Footer /></ProtectedRoute>} />
      <Route path="/messages" element={<ProtectedRoute><Navbar /><Messages /><Footer /></ProtectedRoute>} />
      <Route path="/messages/:convId" element={<ProtectedRoute><Navbar /><Messages /><Footer /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
      <Route path="/dealer" element={<ProtectedRoute><Navbar /><DealerDashboard /><Footer /></ProtectedRoute>} />
      <Route path="/seller/:userId" element={<><Navbar /><SellerProfile /><Footer /></>} />
      <Route path="/buy-slots" element={<ProtectedRoute><Navbar /><BuySlots /><Footer /></ProtectedRoute>} />
      <Route path="/payout-settings" element={<ProtectedRoute><Navbar /><PayoutSettings /><Footer /></ProtectedRoute>} />
      <Route path="/help" element={<><Navbar /><Help /><Footer /></>} />
      <Route path="/legal" element={<><Navbar /><Legal /><Footer /></>} />
      <Route path="/blog" element={<><Navbar /><Blog /><Footer /></>} />
      <Route path="/blog/:slug" element={<><Navbar /><BlogPost /><Footer /></>} />
      <Route path="/referral" element={<><Navbar /><Referral /><Footer /></>} />
      <Route path="/disputes" element={<ProtectedRoute><Navbar /><Disputes /><Footer /></ProtectedRoute>} />
      <Route path="/disputes/:disputeId" element={<ProtectedRoute><Navbar /><DisputeDetail /><Footer /></ProtectedRoute>} />
      {/* SEO City Landing Pages */}
      <Route path="/cars/:citySlug" element={<><Navbar /><CityLanding /><Footer /></>} />
      <Route path="/parts/:citySlug" element={<><Navbar /><CityLanding /><Footer /></>} />
      <Route path="/trust" element={<><Navbar /><Trust /><Footer /></>} />
      <Route path="/about" element={<><Navbar /><About /><Footer /></>} />
      <Route path="/press" element={<><Navbar /><Press /><Footer /></>} />
      <Route path="/careers" element={<><Navbar /><Careers /><Footer /></>} />
      <Route path="/index" element={<><Navbar /><AutoTrustIndex /><Footer /></>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <div className="grain-overlay min-h-screen">
            <AppRouter />
            <ChatBot />
            <PWAInstallPrompt />
            <Toaster richColors position="top-right" />
          </div>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
